import React, { useEffect, useState } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import Drawer from "@/components/ui/drawer";
import Input from "@/components/ui/input";
import Chip from "@/components/ui/chip";
import {
  DocumentModal,
  User,
  Building,
  Asset,
  Document as DocumentIcon,
  Calendar,
  Phone,
  Download,
  DocsFile,
  Sent,
} from "@/components/icons";
import type {
  SupportTicketDetailResponse,
  SupportTicketFile,
} from "../types/support-tickets.types";
import {
  SupportTicketStatus,
  SUPPORT_TICKET_STATUS_CHIP_TYPE,
  SUPPORT_TICKET_STATUS_LABELS_TR,
} from "../types/support-tickets.types";
import { supportTicketsApi } from "../services/support-tickets.api";
import { toast } from "sonner";
import styles from "./SupportTicketDetailDrawer.module.scss";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDetailDateTime(isoOrDisplay: string): string {
  const d = new Date(isoOrDisplay);
  if (Number.isNaN(d.getTime())) return isoOrDisplay;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

export interface SupportTicketDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string | null;
  onTicketUpdated?: () => void;
}

const SupportTicketDetailDrawer: React.FC<SupportTicketDetailDrawerProps> = ({
  isOpen,
  onClose,
  ticketId,
  onTicketUpdated,
}) => {
  const { t } = useTranslation();
  const [ticket, setTicket] = useState<SupportTicketDetailResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [replySending, setReplySending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyInput, setReplyInput] = useState("");
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!isOpen || !ticketId) {
      setTicket(null);
      setError(null);
      setReplyInput("");
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    supportTicketsApi
      .getById(ticketId)
      .then((data) => {
        if (!cancelled) setTicket(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message ?? i18n.t("supportTicketDetail.loadFailed"));
          setTicket(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, ticketId]);

  const fetchTicket = () => {
    if (!ticketId) return;
    supportTicketsApi
      .getById(ticketId)
      .then((data) => {
        setTicket(data);
        setError(null);
      })
      .catch((err) => setError(err?.message ?? i18n.t("supportTicketDetail.loadFailed")));
  };

  const handleDownloadFile = async (file: SupportTicketFile) => {
    // downloadUrl çoğu zaman yalnızca ticket.files[] içinde gelir; mesajın file alanında olmayabilir.
    const fileWithUrl = file.downloadUrl
      ? file
      : (ticket?.files?.find((f) => f.id === file.id) ?? file);
    const downloadUrl = fileWithUrl.downloadUrl;
    if (!downloadUrl) {
      toast.error(t("library.download.noUrl"));
      return;
    }
    setDownloadingFileId(file.id);
    try {
      await supportTicketsApi.downloadFile({ ...file, downloadUrl });
      toast.success(t("supportTickets.detail.downloadSuccess"));
    } catch (err) {
      toast.error(
        (err as Error)?.message ?? t("supportTickets.detail.downloadError"),
      );
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleSendReply = async () => {
    const content = replyInput.trim();
    if (!ticketId || !ticket) return;
    if (ticket.status === SupportTicketStatus.RESOLVED) return; // Çözülen talep kilitli
    if (!content) return;
    setReplySending(true);
    try {
      await supportTicketsApi.postReply(ticketId, { content });
      setReplyInput("");
      fetchTicket(); // Thread'i tazele (yeni mesaj eklenir)
      onTicketUpdated?.();
    } catch (err) {
      // Gönderim hatasını toast ile göster; thread görünür kalsın (setError tüm içeriği gizliyordu).
      toast.error(
        (err as Error)?.message ?? i18n.t("supportTicketDetail.replyFailed"),
      );
    } finally {
      setReplySending(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!ticketId || !ticket) return;
    setClosing(true);
    try {
      await supportTicketsApi.patchStatus(ticketId, 3);
      toast.success(t("supportTickets.detail.closedSuccess"));
      fetchTicket();
      onTicketUpdated?.();
    } catch (err) {
      toast.error(
        (err as Error)?.message ?? t("supportTickets.detail.closeError"),
      );
    } finally {
      setClosing(false);
    }
  };

  if (!ticketId) return null;

  const headerTitle = (
    <span className={styles.headerTitle}>
      <span className={styles.headerIcon}>
        <DocumentModal width={24} height={24} color="#fff" />
      </span>
      <span className={styles.headerTitleText}>
        {ticket ? `${ticket.requestNo} ${ticket.title ?? ""}` : "—"}
      </span>
    </span>
  );

  const isResolved = ticket?.status === SupportTicketStatus.RESOLVED;
  const canReply = Boolean(ticket) && !isResolved;

  const footer = canReply ? (
    <div className={styles.footer}>
      <div className={styles.inputWrap}>
        <Input
          variant="outlined"
          placeholder={t("supportTickets.detail.replyPlaceholder")}
          value={replyInput}
          maxLength={10000}
          onChange={(e) => setReplyInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendReply();
            }
          }}
          disabled={replySending}
        />
      </div>
      <button
        type="button"
        className={styles.sendButton}
        onClick={handleSendReply}
        disabled={replySending || !replyInput.trim()}
        aria-label={t("common.send")}
      >
        <Sent width={20} height={20} color="#fff" />
      </button>
    </div>
  ) : undefined;

  const renderBubbleFile = (file: SupportTicketFile) => (
    <div className={styles.bubbleFile}>
      <DocsFile width={20} height={20} className={styles.attachmentIcon} />
      <div className={styles.attachmentInfo}>
        <span className={styles.attachmentName}>{file.originalName}</span>
        <span className={styles.attachmentSize}>
          {formatFileSize(file.size)}
        </span>
      </div>
      <button
        type="button"
        className={styles.downloadButton}
        onClick={() => handleDownloadFile(file)}
        disabled={downloadingFileId === file.id}
        aria-label={t("buttons.download")}
      >
        <Download width={20} height={20} color="#e7212b" />
      </button>
    </div>
  );

  const visibleMessages = (ticket?.messages ?? []).filter(
    (m) => m.content || m.file,
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={headerTitle}
      width="568px"
      position="right"
      footer={footer}
    >
      <div className={styles.drawerContent}>
        {isLoading && (
          <div className={styles.loadingState}>{t("common.loading")}</div>
        )}
        {error && <div className={styles.errorState}>{error}</div>}
        {!isLoading && !error && ticket && (
          <>
            <div className={styles.fieldRow}>
              <span className={styles.fieldLabel}>
                {t("supportTickets.detail.status")}
              </span>
              <Chip
                type={SUPPORT_TICKET_STATUS_CHIP_TYPE[ticket.status]}
                size="small"
                variant="filled"
              >
                {SUPPORT_TICKET_STATUS_LABELS_TR[ticket.status]}
              </Chip>
              {ticket.status === SupportTicketStatus.IN_REVIEW && (
                <button
                  type="button"
                  onClick={handleCloseTicket}
                  disabled={closing}
                  style={{
                    marginLeft: "auto",
                    padding: "6px 14px",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: "#047857",
                    background: "#d1fae5",
                    border: "1px solid #a7f3d0",
                    borderRadius: "8px",
                    cursor: closing ? "not-allowed" : "pointer",
                    opacity: closing ? 0.6 : 1,
                    transition: "background 0.15s ease",
                  }}
                >
                  {closing
                    ? t("common.loading")
                    : t("supportTickets.detail.close")}
                </button>
              )}
            </div>
            <div className={styles.fieldsGrid}>
              <div className={styles.fieldItem}>
                <User width={18} height={18} className={styles.fieldIcon} />
                <span className={styles.fieldLabel}>
                  {t("supportTickets.detail.sender")}
                </span>
                <span className={styles.fieldValue}>
                  {[ticket.sender.firstName, ticket.sender.lastName]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </span>
              </div>
              <div className={styles.fieldItem}>
                <Building width={18} height={18} className={styles.fieldIcon} />
                <span className={styles.fieldLabel}>
                  {t("supportTickets.detail.department")}
                </span>
                <span className={styles.fieldValue}>
                  {ticket.sender.departmentName ?? "—"}
                </span>
              </div>
              <div className={styles.fieldItem}>
                <Asset width={18} height={18} className={styles.fieldIcon} />
                <span className={styles.fieldLabel}>
                  {t("supportTickets.detail.title")}
                </span>
                <span className={styles.fieldValue}>
                  {ticket.sender.title ?? "—"}
                </span>
              </div>
              <div className={styles.fieldItem}>
                <DocumentIcon
                  width={18}
                  height={18}
                  className={styles.fieldIcon}
                />
                <span className={styles.fieldLabel}>
                  {t("supportTickets.detail.email")}
                </span>
                <span className={styles.fieldValue}>
                  {ticket.sender.email ?? "—"}
                </span>
              </div>
              <div className={styles.fieldItem}>
                <Phone width={18} height={18} className={styles.fieldIcon} />
                <span className={styles.fieldLabel}>
                  {t("supportTickets.detail.phone")}
                </span>
                <span className={styles.fieldValue}>
                  {ticket.sender.phoneNumber ?? "—"}
                </span>
              </div>
              <div className={styles.fieldItem}>
                <Calendar width={18} height={18} className={styles.fieldIcon} />
                <span className={styles.fieldLabel}>
                  {t("supportTickets.detail.date")}
                </span>
                <span className={styles.fieldValue}>
                  {formatDetailDateTime(ticket.createdAt)}
                </span>
              </div>
            </div>

            {/* Karşılıklı konuşma (thread): personel mesajları sağda, talep sahibi solda */}
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {t("supportTickets.detail.conversation")}
              </h4>
              <div className={styles.thread}>
                {visibleMessages.length === 0 && (
                  <div className={styles.sectionBodyBox}>
                    {ticket.description ?? "—"}
                  </div>
                )}
                {visibleMessages.map((m) => (
                  <div
                    key={m.id}
                    className={`${styles.msgRow} ${
                      m.isStaff ? styles.msgRowStaff : styles.msgRowClient
                    }`}
                  >
                    <div
                      className={`${styles.bubble} ${
                        m.isStaff ? styles.bubbleStaff : styles.bubbleClient
                      }`}
                    >
                      <div className={styles.bubbleMeta}>
                        {(m.senderName ||
                          (m.isStaff
                            ? t("supportTickets.detail.staffLabel")
                            : t("supportTickets.detail.clientLabel"))) +
                          " · " +
                          formatDetailDateTime(m.createdAt)}
                      </div>
                      {m.content && (
                        <div className={styles.bubbleContent}>{m.content}</div>
                      )}
                      {m.file && renderBubbleFile(m.file)}
                    </div>
                  </div>
                ))}
              </div>
              {isResolved && (
                <div className={styles.lockedNote}>
                  {t("supportTickets.detail.locked")}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default SupportTicketDetailDrawer;
